import * as A from 'astronomy-engine';
import { SkyFrame, eqjToEnuMatrix } from '../astro/frame';
import { MOON_FEATURES } from '../data/moonFeatures';
import { BODY_COLOR, BodyState, computeBodies } from '../astro/solar';
import { limitingMagnitude } from '../astro/skybrightness';
import { ArabicName, ArabicSky, STATION_WIDTH, fromEcliptic, loadArabicSky, stationZero } from '../data/arabicSky';
import { APP_NAME, APP_NAME_AR } from '../data/brand';
import { Culture, isSharedCulture, loadCulture } from '../data/cultures';
import { ConstellationData, DeepSky, StarCatalog, loadDeepStars, starName } from '../data/catalog';
import {
  DEG, Mat3, Vec3, altAzToEnu, clamp, cross, dot, enuToAltAz, mat3Apply, norm, raDecToVec, refractEnu, smoothstep, vecToRaDec,
} from '../lib/math';
import { limitNudge, measuredSqm } from '../live/skycheck';
import { crescentDay, saudiStarts } from '../live/saudi';
import { OrientationSource } from '../sensors/orientation';
import { Selection, alignReticle, crescentFrame, fovDeg, hideLabels, huntReticle, place, surveyShown, pointingAt, previewFrom, selKey, selection, settings, simNow, skyBand, timeRate, viewMode, labelScale } from '../state';
import { LUT_H, LUT_W, NIGHT_SKY, buildSkyTable, directTransmittance } from './atmosphere';
import { Camera } from './camera';
import { GL, Program, loadTexture } from './gl';
import { BodyDraw, BodyLayer } from './layers/bodies';
import { LineSet } from './layers/lines';
import { PointLayer } from './layers/points';
import { SkyLayer } from './layers/sky';
import { GaiaStars } from './layers/gaia';
import { SurveyLayer } from './layers/survey';
import { StarLayer } from './layers/stars';
import { DSO_EYE, EXT_K, closeCompanions, eyeSeesAt, eyeSkyFor, milkyWaySeen, nameBudget, seenMag, type EyeSky } from './nakedeye';
import { ARABIC_FAMILY, FAMILY, LabelCandidate, LabelHit, drawLabels, drawTag, tone } from './overlay';
import {
  ConstellationGeometry, ECLIPTIC_POLE, buildBorders, buildConstellations, buildFigures, buildGreatCircle, buildLunarStations, buildSphericalGrid,
} from './scene';

/** Everything a moving-object provider needs for one frame. */
export interface FrameContext {
  date: Date;
  frame: SkyFrame;
  camera: Camera;
  dpr: number;
  sunEnu: Vec3;
  sunAlt: number;
  darkness: number; // 0 day … 1 full night
  /** The faintest magnitude drawn: in Full sky the eye's limit with the display and zoom boosts, in Naked eye `nelm` itself. */
  magLimit: number;
  /**
   * The eye's limit: the faintest star (magnitude) the eye can see overhead tonight, from the sky's brightness (the
   * darkness check's measurement where there is one, else the light-pollution estimate), twilight and moonlight, with
   * tonight's "I can't see it" or "I can see more" nudge; no zoom or display boost, in either mode. Lower down a star
   * must be brighter, for the air it shines through (nakedeye.ts, seenMag). The darkness check reads it.
   */
  nelm: number;
  /** The eye's sky tonight (nakedeye.ts): `nelm` overhead, and the limit in any direction, lower where the sky glows
   *  brighter (low down, near the Moon). Naked eye's test for every layer is eyeSeesAt with it. */
  eyeSky: EyeSky;
  /** Naked eye: only what the eye can see is shown, every layer included (see nakedeye.ts). Off is Full sky. */
  nakedEye: boolean;
  time: number; // seconds, monotonic
}

export interface PickHit {
  sel: Selection;
  dist: number; // CSS px from the tap
}

export interface SkyProvider {
  update(ctx: FrameContext, dt: number): void;
  points?(): { data: Float32Array; n: number } | null;
  trails?(): { segments: Float32Array; colors: Float32Array } | null;
  labels?(ctx: FrameContext, out: LabelCandidate[]): void;
  pick?(ctx: FrameContext, x: number, y: number): PickHit | null;
  overlay?(g: CanvasRenderingContext2D, ctx: FrameContext): void;
  /** Screen position of a selection this provider owns (for rings and arrows). */
  locate?(sel: Selection, ctx: FrameContext): { enu: Vec3; label: string } | null;
}

const T_ZENITH = directTransmittance(Math.PI / 2);

/** How eclipses change the light: the Sun's share of daylight left, the Moon's, and the shadow on the Moon. */
interface Eclipse {
  sunLight: number;
  moonLight: number;
  lunar: { offsetDir: Vec3; sep: number; moonR: number; umbra: number; penumbra: number } | null;
}
const NO_ECLIPSE: Eclipse = { sunLight: 1, moonLight: 1, lunar: null };

/** Area of overlap of two discs (radii a, b, centres d apart) as a fraction of disc a. */
function overlapFraction(a: number, b: number, d: number): number {
  if (d >= a + b) return 0;
  if (d <= Math.abs(a - b)) return b >= a ? 1 : (b * b) / (a * a);
  const x = (d * d + a * a - b * b) / (2 * d * a), y = (d * d + b * b - a * a) / (2 * d * b);
  const area = a * a * Math.acos(clamp(x, -1, 1)) + b * b * Math.acos(clamp(y, -1, 1)) - 0.5 * Math.sqrt(Math.max(0, (-d + a + b) * (d + a - b) * (d - a + b) * (d + a + b)));
  return area / (Math.PI * a * a);
}

function eclipseNow(date: Date, sun: BodyState, moon: BodyState): Eclipse {
  const angle = (u: Vec3, v: Vec3) => Math.acos(clamp(dot(u, v), -1, 1)) / DEG;
  // Solar: how much of the Sun's disc the Moon covers, from here. Even at totality some light
  // reaches the sky from beyond the shadow, roughly like deep twilight.
  const rs = sun.angDiamDeg / 2, rm = moon.angDiamDeg / 2;
  const cover = overlapFraction(rs, rm, angle(sun.dir, moon.dir));
  const sunLight = Math.max(1 - cover, 2e-4);
  // Lunar: Earth's shadow at the Moon's distance (Danjon's rule: shadow enlarged 2% by the atmosphere).
  const m = A.GeoVector(A.Body.Moon, date, true), s = A.GeoVector(A.Body.Sun, date, true);
  const mv: Vec3 = [m.x, m.y, m.z], sv: Vec3 = [s.x, s.y, s.z];
  const dm = Math.hypot(...mv) * 149597870.7, ds = Math.hypot(...sv);
  const mu = norm(mv), anti = norm([-sv[0], -sv[1], -sv[2]]);
  const sep = angle(mu, anti);
  const parM = Math.asin(6378.137 / dm) / DEG, parS = 8.794 / 3600 / ds, sdS = 959.63 / 3600 / ds;
  const umbra = 1.02 * (parM + parS - sdS), penumbra = 1.02 * (parM + parS + sdS);
  const moonR = Math.asin(1737.4 / dm) / DEG;
  if (sep > penumbra + moonR) return { sunLight, moonLight: 1, lunar: null };
  // Direction from the Moon towards the shadow centre, in the sky.
  const k = dot(mu, anti);
  const offsetDir = norm([anti[0] - mu[0] * k, anti[1] - mu[1] * k, anti[2] - mu[2] * k]);
  const inUmbra = overlapFraction(moonR, umbra, sep), inPen = overlapFraction(moonR, penumbra, sep);
  const moonLight = Math.max(0.0005, 1 - 0.995 * inUmbra - 0.45 * (inPen - inUmbra));
  return { sunLight, moonLight, lunar: { offsetDir, sep, moonR, umbra, penumbra } };
}
/** People who ask for reduced motion get instant view changes and no twinkling. */
const reducedMotion = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
const motionDur = (d: number) => (reducedMotion?.matches ? 0.01 : d);
/** Whether two selections name the same thing, whatever order their keys were written in. */
const sameSel = (a: Selection, b: Selection) =>
  a.kind === b.kind && Object.keys(a).every((k) => (a as Record<string, unknown>)[k] === (b as Record<string, unknown>)[k]);
type Insets = { top: number; right: number; bottom: number; left: number };
/** Safe-area insets (CSS px) from the page's --safe-* properties, resolved through a probe where they read back as env(). */
function safeArea(): Insets {
  const sides = ['top', 'right', 'bottom', 'left'] as const;
  const cs = getComputedStyle(document.documentElement);
  const px = sides.map((k) => parseFloat(cs.getPropertyValue(`--safe-${k}`)));
  if (px.some(Number.isNaN) && cs.getPropertyValue('--safe-top').trim()) {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding:var(--safe-top) var(--safe-right) var(--safe-bottom) var(--safe-left)';
    document.body.appendChild(probe);
    const ps = getComputedStyle(probe);
    sides.forEach((k, i) => (px[i] = parseFloat(ps.getPropertyValue(`padding-${k}`))));
    probe.remove();
  }
  const [top, right, bottom, left] = px.map((v) => (Number.isFinite(v) ? v : 0));
  return { top, right, bottom, left };
}
let L_DAY = 0;
/** Zenith radiance with the Sun 45° up: the reference for full daylight adaptation. */
function dayZenith() {
  if (!L_DAY) L_DAY = buildSkyTable(Math.PI / 4)[((LUT_H - 1) * LUT_W) * 4 + 1];
  return L_DAY;
}
// Sky brightness and the naked-eye limit are worked out in astro/skybrightness, which the events worker shares.
export { limitingMagnitude, skyBrightness } from '../astro/skybrightness';

export class SkyEngine {
  readonly camera = new Camera();
  readonly frame = new SkyFrame();
  readonly orientation = new OrientationSource();
  gl: GL;
  providers: SkyProvider[] = [];
  bodies: BodyState[] = [];
  stars!: StarCatalog;
  cons!: ConstellationData;
  consGeo!: ConstellationGeometry;
  dsos: DeepSky[] = [];
  /** The Arabic sky culture, once loaded: figures, their brightest member's magnitude, star names and station centres. */
  arabic: { sky: ArabicSky; geo: ConstellationGeometry; minMag: number[]; starNames: Map<number, ArabicName[]>; stations: Vec3[] } | null = null;
  private arabicLoading: Promise<void> | null = null;
  /** Another sky culture (Norse, Chinese…), once loaded. */
  culture: { data: Culture; geo: ConstellationGeometry; minMag: number[]; starNames: Map<number, { en?: string; native?: string; pron?: string }> } | null = null;
  private cultureLoading: { id: string; p: Promise<void> } | null = null;
  private cultureFailed: { id: string; until: number } | null = null;
  /** Whether the Moon's close-up maps have been requested (and when to try again after a failure). */
  private moonDetail = false;
  /** Whether it is the 29th, the 30th or the 1st here by Saudi Arabia's calendar, worked out once a minute
   *  (src/live/saudi.ts), and again whenever Saudi Arabia's month starts change. */
  private crescentNow = { key: '', starts: saudiStarts.peek(), on: false };
  private moonDetailRetry = 0;
  private bandTextures = new Map<string, Promise<WebGLTexture | null>>();
  private bandShown: string | null = null;
  /** Label boxes from the last frame, so names can be tapped. */
  private labelHits: LabelHit[] = [];
  ready = false;
  onError?: (e: Error) => void;
  onFrame?: (ctx: FrameContext) => void;

  // Manual view state (degrees).
  viewAz = 180;
  viewAlt = window.innerHeight > window.innerWidth ? 48 : 35;
  private anim: { fromAz: number; fromAlt: number; toAz: number; toAlt: number; fromFov: number; toFov: number; t: number; dur: number } | null = null;
  velAz = 0;
  velAlt = 0;
  interacting = false;

  private sky: SkyLayer;
  private starLayer!: StarLayer;
  /** Fainter stars (magnitude 8–10), loaded the first time the view zooms in. */
  private deepLayer: StarLayer | null = null;
  /** Photographic sky survey tiles, created on first zoom-in. */
  private survey: SurveyLayer | null = null;
  /** Faint Gaia stars in tiles, created on first deep zoom. */
  private gaia: GaiaStars | null = null;
  /** The fainter stars: a load under way; after a failure, when to try again, how many have failed, and whether offline. */
  private deepLoading = false;
  private deepRetryAt = 0;
  private deepFailures = 0;
  private deepFailedOffline = false;
  private bodyLayer: BodyLayer;
  private points: PointLayer;
  private consLines: LineSet;
  private arLines: LineSet;
  private lunarLines: LineSet;
  private cultureLines: LineSet;
  private borderLines: LineSet;
  private gridAltAz: LineSet;
  private gridEq: LineSet;
  private ecliptic: LineSet;
  private trails: LineSet;
  private g2d: CanvasRenderingContext2D;
  private raf = 0;
  private lastT = 0;
  private clock = 0;
  private bodiesAt = 0;
  private bodiesPlaceKey = '';
  private dprGL = 1;
  private dpr2D = 1;
  /** Safe-area insets, CSS px (labels stay inside them). */
  private safe: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
  /** The screen's safe-area insets (CSS px): what the phone's own bars cover. */
  get insets(): Insets {
    return this.safe;
  }
  private enuToCamF = new Float32Array(9);
  private eqjToEnuF = new Float32Array(9);
  private eqdFrameF = new Float32Array(9);
  private ctx!: FrameContext;
  private lastPointingCheck = 0;

  constructor(private canvas: HTMLCanvasElement, private overlay: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL2 is not available on this device.');
    this.gl = gl;
    this.g2d = overlay.getContext('2d')!;
    this.sky = new SkyLayer(gl);
    this.bodyLayer = new BodyLayer(gl);
    this.points = new PointLayer(gl);
    this.consLines = new LineSet(gl);
    this.arLines = new LineSet(gl);
    this.lunarLines = new LineSet(gl);
    this.cultureLines = new LineSet(gl);
    this.borderLines = new LineSet(gl);
    this.gridAltAz = new LineSet(gl);
    this.gridEq = new LineSet(gl);
    this.ecliptic = new LineSet(gl);
    this.trails = new LineSet(gl);
    this.gridAltAz.setData(buildSphericalGrid(10, 15));
    this.gridEq.setData(buildSphericalGrid(10, 15));
    this.ecliptic.setData(buildGreatCircle(ECLIPTIC_POLE));
    canvas.addEventListener('webglcontextlost', (e) => e.preventDefault());
    canvas.addEventListener('webglcontextrestored', () => location.reload());
  }

  async load(data: { stars: StarCatalog; cons: ConstellationData; dsos: DeepSky[] }) {
    this.stars = data.stars;
    this.cons = data.cons;
    this.dsos = data.dsos;
    this.starLayer = new StarLayer(this.gl, data.stars);
    this.starLayer.setMagnitudes(data.stars.mag);
    this.consGeo = buildConstellations(data.cons, data.stars);
    this.consLines.setData(this.consGeo.segments, this.consGeo.gaps);
    this.borderLines.setData(buildBorders(data.cons));
    this.ready = true;
    const base = import.meta.env.BASE_URL;
    const big = Math.min(window.screen.width, window.screen.height) * (window.devicePixelRatio || 1) > 900;
    loadTexture(this.gl, `${base}tex/milkyway-${big ? '4k' : '2k'}.webp`, { repeatS: true })
      .then((t) => (this.sky.mwTex = t))
      .catch((e) => console.warn(e));
    loadTexture(this.gl, `${base}tex/moon-2k.webp`, { repeatS: true })
      .then((t) => (this.bodyLayer.moonTex = t))
      .catch((e) => console.warn(e));
  }

  /** Load the Arabic sky culture the first time it is needed. */
  ensureArabic(): Promise<void> {
    if (this.arabic) return Promise.resolve();
    if (this.arabicLoading) return this.arabicLoading;
    return (this.arabicLoading = loadArabicSky()
      .then((sky) => {
        const cat = this.stars;
        const geo = buildFigures(sky.figures, cat, this.dsos);
        const minMag = geo.stars.map((m) => m.reduce((a, i) => Math.min(a, cat.mag[i]), 99));
        const starNames = new Map<number, ArabicName[]>();
        for (const [hip, names] of Object.entries(sky.stars)) {
          const i = cat.byHip.get(Number(hip));
          if (i !== undefined) starNames.set(i, names);
        }
        const a = cat.byHip.get(sky.lunar.anchorHip);
        const zero = a !== undefined ? stationZero(sky, this.starDir(a)) : 60 - sky.lunar.offset;
        this.arLines.setData(geo.segments, geo.gaps);
        this.lunarLines.setData(buildLunarStations(sky, zero));
        const stations = sky.lunar.names.map((_, p) => fromEcliptic(zero + (p + 0.5) * STATION_WIDTH, sky.lunar.extent[2 * p + 1] * 0.6));
        this.arabic = { sky, geo, minMag, starNames, stations };
      })
      .catch((e) => {
        console.warn(e);
        this.arabicLoading = null;
      }));
  }

  /** Load another sky culture (Norse, Chinese…) the first time it is chosen. */
  ensureCulture(id: string): Promise<void> {
    if (this.culture?.data.id === id) return Promise.resolve();
    if (this.cultureLoading?.id === id) return this.cultureLoading.p;
    if (this.cultureFailed?.id === id && Date.now() < this.cultureFailed.until) return Promise.resolve();
    const p = Promise.all([loadCulture(id), loadDeepStars().catch(() => null)])
      .then(([data, deep]) => {
        if (settings.peek().skyCulture !== id) return; // chosen something else meanwhile
        const cat = this.stars;
        // Figure stars fainter than the main catalogue come from the Tycho-2 file.
        const geo = buildFigures(data.figures, cat, this.dsos, deep);
        const minMag = geo.stars.map((m) => m.reduce((a, i) => Math.min(a, cat.mag[i]), 99));
        const starNames = new Map<number, { en?: string; native?: string; pron?: string }>();
        for (const [hip, n] of Object.entries(data.stars)) {
          const i = cat.byHip.get(Number(hip));
          if (i !== undefined) starNames.set(i, n);
        }
        this.cultureLines.setData(geo.segments, geo.gaps);
        this.culture = { data, geo, minMag, starNames };
      })
      .catch((e) => {
        console.warn(e);
        this.cultureFailed = { id, until: Date.now() + 60000 }; // offline or missing: try again in a minute
      })
      .finally(() => {
        if (this.cultureLoading?.id === id) this.cultureLoading = null;
      });
    this.cultureLoading = { id, p };
    return p;
  }

  /** When the view has been still for a few seconds, draw at about 30 frames a second to save battery. */
  private lastBusy = 0;

  start() {
    const loop = (t: number) => {
      this.raf = requestAnimationFrame(loop);
      const busy = this.interacting || !!this.anim || !!this.velAz || !!this.velAlt || viewMode.peek() === 'ar' || Math.abs(timeRate.peek()) > 1;
      if (busy) this.lastBusy = t;
      if (t - this.lastBusy > 2500 && this.lastT && t - this.lastT < 30) return;
      const dt = this.lastT ? Math.min(0.1, (t - this.lastT) / 1000) : 1 / 60;
      this.lastT = t;
      try {
        this.step(dt);
      } catch (e) {
        cancelAnimationFrame(this.raf);
        this.onError?.(e as Error);
        throw e;
      }
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    this.orientation.stop();
  }

  resize() {
    const r = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this.dprGL = Math.min(dpr, 2);
    this.dpr2D = Math.min(dpr, 3);
    this.canvas.width = Math.round(r.width * this.dprGL);
    this.canvas.height = Math.round(r.height * this.dprGL);
    this.overlay.width = Math.round(r.width * this.dpr2D);
    this.overlay.height = Math.round(r.height * this.dpr2D);
    this.camera.setViewport(r.width, r.height);
    this.safe = safeArea();
  }

  /** Render a frame and combine the sky and its labels into one image. */
  async snapshot(caption: string): Promise<Blob> {
    // The Arabic name is drawn in its own face: make sure it has arrived before the canvas asks for it.
    await document.fonts?.load(`20px "Adwa as-Salaf"`, APP_NAME_AR).catch(() => undefined);
    this.step(0);
    const w = this.canvas.width, h = this.canvas.height;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d')!;
    g.drawImage(this.canvas, 0, 0);
    g.drawImage(this.overlay, 0, 0, w, h);
    const s = w / this.camera.width;
    const pad = 16 * s;
    const grad = g.createLinearGradient(0, h - 90 * s, 0, h);
    grad.addColorStop(0, 'rgba(4,6,12,0)');
    grad.addColorStop(1, 'rgba(4,6,12,0.85)');
    g.fillStyle = grad;
    g.fillRect(0, h - 90 * s, w, 90 * s);
    // The name with its Arabic beside it, on one baseline, as on the welcome.
    g.fillStyle = 'rgba(243,211,140,0.95)';
    g.font = `600 ${15 * s}px ${FAMILY}`;
    g.textBaseline = 'alphabetic';
    const name = APP_NAME.toUpperCase();
    g.fillText(name, pad, h - pad - 22 * s);
    const nameEnd = pad + g.measureText(name).width;
    g.font = `500 ${20 * s}px ${ARABIC_FAMILY}`;
    g.fillText(APP_NAME_AR, nameEnd + 10 * s, h - pad - 22 * s);
    g.fillStyle = 'rgba(233,237,248,0.85)';
    g.font = `500 ${13 * s}px ${FAMILY}`;
    g.textBaseline = 'bottom';
    g.fillText(caption, pad, h - pad);
    return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('Snapshot failed'))), 'image/jpeg', 0.92));
  }

  // ---------- View control ----------
  /**
   * Animate the manual view to an ENU direction. `raise` (0..0.5) places the target that fraction
   * of the screen height above the centre, so a bottom sheet does not cover it.
   */
  flyTo(enu: Vec3, fov?: number, dur = 1.1, raise = 0) {
    let { alt, az } = enuToAltAz(refractEnu(enu));
    if (raise) {
      const f = fov ?? fovDeg.peek();
      const vfov = f * Math.max(1, this.camera.height / this.camera.width);
      alt = alt - raise * vfov;
    }
    let toAz = az;
    while (toAz - this.viewAz > 180) toAz -= 360;
    while (toAz - this.viewAz < -180) toAz += 360;
    this.anim = {
      fromAz: this.viewAz, fromAlt: this.viewAlt, toAz, toAlt: clamp(alt, settings.peek().seeThroughGround ? -85 : Math.min(5, this.viewAlt), 89.5),
      fromFov: fovDeg.peek(), toFov: fov ?? fovDeg.peek(), t: 0, dur: motionDur(dur),
    };
    this.velAz = this.velAlt = 0;
  }

  /**
   * Fly so a direction sits in the middle of a screen region (CSS px), e.g. the sky left visible above an
   * open card. The view never dips below `lowest` (+5°) unless the ground is see-through. Manual mode only.
   */
  frameInView(enu: Vec3, region: { x0: number; y0: number; x1: number; y1: number }, fov?: number, dur = 0.9, lowest = 5) {
    const cam = this.camera;
    let f = fov ?? fovDeg.peek();
    const half = Math.min(cam.width, cam.height) / 2;
    const toAngle = (px: number) => (2 * Math.atan((px * Math.tan((f * DEG) / 4)) / half)) / DEG;
    const { alt, az } = enuToAltAz(refractEnu(enu));
    const dy = cam.height / 2 - (region.y0 + region.y1) / 2; // px above the screen centre
    const dx = (region.x0 + region.x1) / 2 - cam.width / 2; // px right of the centre
    const floor = settings.peek().seeThroughGround ? -85 : lowest;
    let toAlt = alt - toAngle(dy);
    if (toAlt < floor && dy > 0 && alt > floor) {
      // Low in the sky: rather than tip the view below the horizon, zoom in until it sits in the region.
      const need = ((alt - floor) * DEG) / 2;
      f = clamp((4 * Math.atan((half * Math.tan(need)) / dy)) / DEG, 12, f);
      toAlt = alt - toAngle(dy);
    }
    toAlt = clamp(toAlt, floor, 89.5);
    let toAz = az - toAngle(dx) / Math.max(0.2, Math.cos(clamp(alt, -80, 80) * DEG));
    while (toAz - this.viewAz > 180) toAz -= 360;
    while (toAz - this.viewAz < -180) toAz += 360;
    this.anim = { fromAz: this.viewAz, fromAlt: this.viewAlt, toAz, toAlt, fromFov: fovDeg.peek(), toFov: f, t: 0, dur: motionDur(dur) };
    this.velAz = this.velAlt = 0;
  }

  cancelAnimation() {
    this.anim = null;
  }

  /** Current view centre as alt/az (from AR or manual state). */
  viewCentre(): { alt: number; az: number } {
    return enuToAltAz(this.camera.forward());
  }

  /** Enter manual mode keeping the current AR orientation. */
  syncManualFromCamera() {
    const { alt, az } = this.viewCentre();
    this.viewAlt = alt;
    this.viewAz = az;
  }

  // ---------- Per-frame ----------
  /** The 29th, the 30th or the 1st here, when the Moon's label says nothing of its light. */
  private isCrescentDay(): boolean {
    const t = simNow().getTime(), pl = place.peek(), starts = saudiStarts.peek();
    // A month start heard late (the sky paused, say) changes the date at the same minute and place.
    const key = `${Math.floor(t / 60e3)}|${pl.lat}|${pl.lon}|${pl.timeZone ?? ''}`;
    if (key !== this.crescentNow.key || starts !== this.crescentNow.starts) this.crescentNow = { key, starts, on: crescentDay(t, pl, starts) };
    return this.crescentNow.on;
  }

  private step(dt: number) {
    if (!this.ready) return;
    this.clock += dt;
    const gl = this.gl;
    const set = settings.peek();
    const date = simNow();
    const pl = place.peek();
    this.frame.update(date, pl);

    // Magnetic declination for the pointing compass: where the phone is (home, while the sky shows another place) and
    // today's field (while it shows another date).
    this.orientation.updateDeclination(previewFrom.peek()?.home ?? pl);

    // Solar system positions: every 2 s of real time when live, every frame when time runs fast.
    const pk = `${pl.lat},${pl.lon},${pl.height}`;
    const rate = Math.abs(timeRate.peek());
    const since = Math.abs(date.getTime() - this.bodiesAt);
    if (pk !== this.bodiesPlaceKey || since > (rate > 30 ? 0 : 2000) || !this.bodies.length) {
      this.bodies = computeBodies(date, this.frame.observer);
      this.bodiesAt = date.getTime();
      this.bodiesPlaceKey = pk;
    }

    // Camera.
    const cam = this.camera;
    const mode = viewMode.peek();
    let fov = fovDeg.peek();
    if (this.anim) {
      const a = this.anim;
      a.t += dt / a.dur;
      const k = a.t >= 1 ? 1 : 1 - Math.pow(1 - a.t, 3);
      this.viewAz = a.fromAz + (a.toAz - a.fromAz) * k;
      this.viewAlt = a.fromAlt + (a.toAlt - a.fromAlt) * k;
      fov = a.fromFov * Math.pow(a.toFov / a.fromFov, k);
      fovDeg.value = fov;
      if (a.t >= 1) this.anim = null;
    } else if (!this.interacting && (this.velAz || this.velAlt)) {
      this.viewAz += this.velAz * dt;
      this.viewAlt = clamp(this.viewAlt + this.velAlt * dt, -89.5, 89.5);
      const damp = Math.exp(-dt * 4.5);
      this.velAz *= damp;
      this.velAlt *= damp;
      if (Math.abs(this.velAz) < 0.05 && Math.abs(this.velAlt) < 0.05) this.velAz = this.velAlt = 0;
    }
    const arMatrix = mode === 'ar' ? this.orientation.camToEnu(dt) : null;
    if (arMatrix) {
      cam.projection = 'perspective';
      cam.fovDeg = set.cameraFeed ? set.cameraFov : Math.min(fov, 110);
      cam.update();
      cam.setOrientation(arMatrix);
    } else {
      cam.projection = 'stereographic';
      cam.fovDeg = fov;
      cam.update();
      cam.lookAt(this.viewAlt, ((this.viewAz % 360) + 360) % 360);
    }

    // Lighting and sky brightness.
    const sun = this.bodies[0], moon = this.bodies[1];
    if (this.eclipseAt !== this.bodiesAt) {
      this.eclipseAt = this.bodiesAt;
      this.eclipse = eclipseNow(date, sun, moon);
    }
    const sunEnu = this.frame.toEnu(sun.dir);
    const moonEnu = this.frame.toEnu(moon.dir);
    const sunAlt = Math.asin(sunEnu[2]) / DEG;
    const moonAlt = Math.asin(moonEnu[2]) / DEG;
    this.sky.updateLights(sunAlt * DEG, moonAlt * DEG, moonAlt > -2);
    const moonScale = moonAlt > -2 ? 2.5e-6 * Math.pow(10, -0.4 * (moon.mag + 12.7)) * this.eclipse.moonLight : 0;
    // The eye's limit tonight (overhead): from the darkness check's measurement of the sky where there is one, else
    // the light-pollution estimate, with tonight's nudge; lower where the sky glows brighter, low down and near the
    // Moon (eyeSky). Naked eye draws to it, whatever the zoom; Full sky boosts the estimate's, as it always has.
    const eyeMode = set.nakedEye;
    const sqmEye = measuredSqm(pl) ?? (set.bortleAuto ? set.sqm : null);
    const eyeSky = eyeSkyFor(sunAlt, moonEnu, moon.mag, set.bortle, sqmEye, limitNudge.peek());
    const nelm = eyeSky.limit;
    const darkness = 1 - smoothstep(-16, -2, sunAlt);
    let magLimit = nelm;
    if (!eyeMode) {
      const zoomBoost = Math.max(0, 2.2 * Math.log10(60 / cam.fovDeg));
      magLimit = limitingMagnitude(sunAlt, moonAlt, moon.mag, set.bortle, set.bortleAuto ? set.sqm : null) + set.starBoost * 0.85 + zoomBoost;
      if (set.starBoost >= 1) magLimit = Math.max(magLimit, 2.2 + zoomBoost * 0.5); // keep the bright stars in daylight
      // Tycho-2 reaches V 10; zoomed in, the Gaia tiles go on to about 13.
      magLimit = Math.min(magLimit, this.deepLayer ? (cam.fovDeg < 15 ? 13 : 10.3) : 8.3);
    }
    if (magLimit > 7.9) this.loadDeepStarsWhenDue();

    const W = this.canvas.width, H = this.canvas.height;
    this.ctx = { date, frame: this.frame, camera: cam, dpr: this.dpr2D, sunEnu, sunAlt, darkness, magLimit, nelm, eyeSky, nakedEye: eyeMode, time: this.clock };
    for (const p of this.providers) p.update(this.ctx, dt);

    // Uniforms shared by the sky-anchored passes.
    const u = cam.uniforms;
    this.eqjToEnuF.set(this.frame.eqjToEnu);
    this.enuToCamF.set(cam.enuToCam);
    const setCommon = (p: Program) => {
      p.m3('u_eqjToEnu', this.eqjToEnuF).m3('u_enuToCam', this.enuToCamF).i1('u_proj', u.proj).f1('u_scale', u.scale);
      p.f2('u_aspectFix', u.aspectFix[0], u.aspectFix[1]).f2('u_viewport', W, H);
    };

    gl.viewport(0, 0, W, H);
    const transparent = mode === 'ar' && set.cameraFeed;
    gl.clearColor(0, 0, 0, transparent ? 0 : 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // The glow of light pollution: in Naked eye, as the person's darkness check measured it where there is one.
    const glowSqm = eyeMode ? sqmEye : set.bortleAuto ? set.sqm : null;
    const lp = glowSqm != null ? clamp((21.95 - glowSqm) / 4.1, 0, 1) : (set.bortle - 1) / 8;
    const nightBase: Vec3 = [0.008 + 0.01 * lp, 0.012 + 0.012 * lp, 0.03 + 0.012 * lp];
    const lpColor: Vec3 = [0.1 * lp * lp + 0.012, 0.075 * lp * lp + 0.012, 0.06 * lp * lp + 0.02];
    const moonFactor = moonAlt > 0 ? 1 - 0.75 * Math.min(1, moonScale / 2.5e-6) * Math.min(1, Math.sin(moonAlt * DEG) * 2) : 1;
    // Zoomed in: the photographic survey fades in (and the smooth Milky Way map, too soft this close, fades out).
    // Not while the sky is shown in another kind of light: the photographs are visible light. Nor in Naked eye: they
    // show far more than any eye. There the Milky Way shows only where the sky is dark enough to see it.
    const surveyMix = set.survey && !eyeMode && !skyBand.peek() ? smoothstep(28, 14, cam.fovDeg) * smoothstep(-4, -14, sunAlt) : 0;
    const mwGain = set.milkyWay ? 1.7 * (1 - 0.8 * lp) * smoothstep(-6, -16, sunAlt) * moonFactor * (1 - 0.7 * surveyMix) * (eyeMode ? milkyWaySeen(nelm) : 1) : 0;
    // Other wavelengths: fade the chosen band in (loading it the first time), or back out.
    const band = skyBand.peek();
    if (band && !this.bandTextures.has(band)) {
      this.bandTextures.set(band, loadTexture(this.gl, `${import.meta.env.BASE_URL}tex/bands/${band}.webp`, { repeatS: true }).catch(() => null));
    }
    if (band && band !== this.bandShown) {
      void this.bandTextures.get(band)!.then((t) => {
        if (skyBand.peek() === band && t) {
          this.sky.bandTex = t;
          this.bandShown = band;
        }
      });
    }
    const target = band && this.bandShown === band ? 1 : 0;
    this.sky.bandMix += (target - this.sky.bandMix) * Math.min(1, dt * 4);
    if (!band && this.sky.bandMix < 0.01) this.bandShown = null;
    const z = this.sky.zenith();
    const lZen = Math.max(z.sun * this.eclipse.sunLight + z.moon * moonScale, 1e-14);
    const ADAPT = 0.78;
    const exposure = (0.95 * Math.pow(dayZenith(), ADAPT - 1)) / Math.pow(lZen, ADAPT);
    // The eye cannot adapt to a twilight fainter than the natural night sky, so the Sun's light is shown no brighter
    // than against that sky and the last of the twilight fades into the night (moonlight keeps its adaptation).
    const twilight = Math.pow(lZen / Math.max(lZen, NIGHT_SKY), ADAPT);
    this.sky.drawSky(setCommon, { sunEnu, moonEnu, moonScale, mwGain, nightBase, lpColor, transparent, exposure, sunScale: this.eclipse.sunLight * twilight });
    // The part of the sky in view, for the photographs.
    const aspect = Math.max(cam.width, cam.height) / Math.min(cam.width, cam.height);
    const half = (cam.fovDeg * DEG) / 2;
    const viewRadius = u.proj === 0 ? Math.atan(Math.tan(half) * Math.hypot(1, aspect)) : 2 * Math.atan(Math.tan(half / 2) * Math.hypot(1, aspect));
    const viewCentre = this.frame.toEqj(cam.forward());
    // Within 6° of the Moon the photographs are hidden, so a view filled by that circle shows none.
    const moonOnly = moonAlt > -2 && Math.acos(clamp(dot(viewCentre, moon.dir), -1, 1)) + viewRadius < 6 * DEG;
    if (surveyMix > 0.01 && !moonOnly) {
      this.survey ??= new SurveyLayer(gl, Math.min(cam.width, cam.height) < 600);
      this.survey.draw(setCommon, {
        centre: viewCentre,
        radius: viewRadius,
        order: SurveyLayer.orderFor(cam.fovDeg, Math.min(cam.width, cam.height), window.devicePixelRatio || 1),
        // Moonlight washes the photograph out, and near the Moon it is hidden altogether.
        gain: surveyMix * 0.95 * (0.35 + 0.65 * moonFactor),
        // While it is only fading in (wide views, dozens of cells), the whole-sky preview is enough.
        fetch: cam.fovDeg < 18,
        avoid: moonAlt > -2 ? [moon.dir[0], moon.dir[1], moon.dir[2], Math.cos(6 * DEG)] : undefined,
      });
    }
    const shown = surveyMix > 0.15 && !moonOnly;
    if (shown !== surveyShown.peek()) surveyShown.value = shown;

    // Reference lines.
    const dprL = this.dprGL;
    const I = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    if (set.grid === 'altaz') this.gridAltAz.draw(setCommon, { frame: I, refract: false, width: 1 * dprL, color: [0.35, 0.55, 0.75, 0.22] });
    if (set.grid === 'equatorial') {
      const rot = A.Rotation_EQD_EQJ(date).rot;
      for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) this.eqdFrameF[r * 3 + c] = rot[c][r];
      const m = new Float32Array(9);
      for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) {
        let s = 0;
        for (let k = 0; k < 3; k++) s += this.frame.eqjToEnu[r * 3 + k] * this.eqdFrameF[k * 3 + c];
        m[r * 3 + c] = s;
      }
      this.gridEq.draw(setCommon, { frame: m, refract: true, width: 1 * dprL, color: [0.45, 0.6, 0.95, 0.22] });
    }
    if (set.ecliptic) this.ecliptic.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1.2 * dprL, color: [0.95, 0.75, 0.4, 0.35] });
    if (set.constellationBounds) this.borderLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1 * dprL, color: [0.55, 0.6, 0.85, 0.2], dash: 6 * dprL });
    const sel = selection.peek();
    const arabic = set.skyCulture === 'arabic';
    if (arabic || set.lunarStations || sel?.kind === 'arabic' || sel?.kind === 'manzil') void this.ensureArabic();
    const shared = isSharedCulture(set.skyCulture) ? set.skyCulture : null;
    if (shared && this.culture?.data.id !== shared) void this.ensureCulture(shared);
    const cul = shared && this.culture?.data.id === shared ? this.culture : null;
    const modern = !arabic && !shared;
    // Figure lines: one CSS pixel, quiet enough to sit behind the names by day and by night.
    const lineAlpha = 0.22;
    const ar = this.arabic;
    if (set.lunarStations && ar) {
      this.lunarLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1 * dprL, color: [0.47, 0.9, 0.75, 0.2 + 0.12 * darkness] });
    }
    if (set.constellationLines && cul) {
      this.cultureLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: dprL, color: [0.5, 0.92, 0.8, lineAlpha * 1.05], gapScale: dprL });
    }
    if (sel?.kind === 'figure' && cul && cul.geo.ranges[sel.index]) {
      const r = cul.geo.ranges[sel.index];
      this.cultureLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1.8 * dprL, color: [0.7, 1.0, 0.9, 0.9], gapScale: dprL, first: r.first, count: r.count });
    }
    if (set.constellationLines && modern) {
      this.consLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: dprL, color: [0.62, 0.7, 0.9, lineAlpha], gapScale: dprL });
    }
    if (set.constellationLines && arabic && ar) {
      this.arLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: dprL, color: [1.0, 0.78, 0.47, lineAlpha * 1.1], gapScale: dprL });
    }
    if (sel?.kind === 'arabic' && ar && ar.geo.ranges[sel.index]) {
      const r = ar.geo.ranges[sel.index];
      this.arLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1.8 * dprL, color: [1.0, 0.86, 0.62, 0.9], gapScale: dprL, first: r.first, count: r.count });
    }
    if (sel?.kind === 'constellation' && this.consGeo.ranges[sel.index]) {
      const r = this.consGeo.ranges[sel.index];
      this.consLines.draw(setCommon, { frame: this.eqjToEnuF, refract: true, width: 1.8 * dprL, color: [0.62, 0.78, 1.0, 0.85], gapScale: dprL, first: r.first, count: r.count });
    }

    // Stars.
    const below = set.seeThroughGround ? 1 : 0;
    this.starLayer.draw(setCommon, {
      magLimit, pxScale: dprL, extK: EXT_K, time: this.clock, twinkle: set.twinkle && !reducedMotion?.matches ? 1 : 0, saturation: 0.75,
      highlight: sel?.kind === 'star' ? sel.index : -1, below, eye: eyeMode ? eyeSky : null,
    });
    if (this.deepLayer && magLimit > 7.9) {
      this.deepLayer.draw(setCommon, { magLimit, pxScale: dprL, extK: 0.25, time: this.clock, twinkle: 0, saturation: 0.75, highlight: -1, below });
    }
    if (this.deepLayer && magLimit > 10 && cam.fovDeg < 15) {
      this.gaia ??= new GaiaStars(gl);
      const a = Math.max(cam.width, cam.height) / Math.min(cam.width, cam.height);
      const half = (cam.fovDeg * DEG) / 2;
      const radius = cam.uniforms.proj === 0 ? Math.atan(Math.tan(half) * Math.hypot(1, a)) : 2 * Math.atan(Math.tan(half / 2) * Math.hypot(1, a));
      this.gaia.draw(setCommon, { centre: this.frame.toEqj(cam.forward()), radius }, { magLimit, pxScale: dprL, extK: 0.25, time: this.clock, twinkle: 0, saturation: 0.75, highlight: -1, below });
    }

    // Satellite trails and points.
    for (const p of this.providers) {
      const t = p.trails?.();
      if (t && t.segments.length) {
        this.trails.setData(t.segments, undefined, t.colors, true);
        this.trails.draw(setCommon, { frame: I, refract: true, width: 1.3 * dprL, color: [1, 1, 1, 1] });
      }
      const pts = p.points?.();
      if (pts && pts.n) {
        this.points.setData(pts.data, pts.n);
        this.points.draw(setCommon);
      }
    }

    // Sun, Moon and planets.
    this.drawBodies(W, H, magLimit, darkness);

    // Ground.
    const day = smoothstep(-8, 6, sunAlt);
    const groundColor: Vec3 = [0.012 + 0.07 * day, 0.014 + 0.08 * day, 0.02 + 0.06 * day];
    const rim: Vec3 = [0.06 + 0.2 * day + 0.1 * lp, 0.07 + 0.25 * day + 0.07 * lp, 0.11 + 0.3 * day + 0.05 * lp];
    if (!transparent) {
      this.sky.drawGround(setCommon, { groundColor, rimColor: rim, alpha: set.seeThroughGround ? 0.72 : 1, landscape: set.landscape });
    }

    this.drawOverlay(magLimit, darkness);
    this.onFrame?.(this.ctx);
  }

  private eclipse: Eclipse = NO_ECLIPSE;
  private eclipseAt = -1;
  private bodyDraws: BodyDraw[] = [];
  private moonScreen: { x: number; y: number; r: number; toBody: Float32Array; light: [number, number, number] } | null = null;
  private pathCache: { key: string; pts: { t: number; enu: Vec3 }[] } | null = null;
  /** Screen info for bodies, reused by labels and picking. `seen`: one the eye can see (always, in Full sky). */
  bodyScreen: { id: string; x: number; y: number; r: number; visible: boolean; seen: boolean; enu: Vec3 }[] = [];

  /**
   * The fainter stars, once the view needs them. Nothing more is asked while a load is under way. After one fails
   * (offline, say: the file is kept for offline use only once it has loaded), the next try waits 10 s, doubling
   * each time up to five minutes, unless the phone has come back online. So never a request on every frame, and
   * the bright stars carry on meanwhile.
   */
  private loadDeepStarsWhenDue() {
    if (this.deepLayer || this.deepLoading) return;
    if (Date.now() < this.deepRetryAt && !(this.deepFailedOffline && navigator.onLine)) return;
    this.deepLoading = true;
    loadDeepStars()
      .then((cat) => {
        const layer = new StarLayer(this.gl, cat);
        layer.setMagnitudes(cat.mag);
        this.deepLayer = layer;
        this.deepFailures = 0;
      })
      .catch((e) => {
        if (!this.deepFailures) console.warn('Fainter stars did not load; trying again later.', e);
        this.deepRetryAt = Date.now() + Math.min(300e3, 10e3 * 2 ** this.deepFailures++);
        this.deepFailedOffline = navigator.onLine === false;
      })
      .finally(() => (this.deepLoading = false));
  }

  private localBasis(camDir: Vec3) {
    const z: Vec3 = [-camDir[0], -camDir[1], -camDir[2]];
    let x: Vec3 = [1 - z[0] * z[0], -z[0] * z[1], -z[0] * z[2]];
    x = norm(x);
    const y = cross(z, x);
    return { x, y, z };
  }

  private toLocal(v: Vec3, basis: { x: Vec3; y: Vec3; z: Vec3 }): [number, number, number] {
    const e = this.frame.toEnu(v);
    const c = mat3Apply(this.camera.enuToCam, e[0], e[1], e[2]);
    return [dot(c, basis.x), dot(c, basis.y), dot(c, basis.z)];
  }

  private drawBodies(W: number, H: number, magLimit: number, darkness: number) {
    const cam = this.camera;
    const dpr = this.dprGL;
    const draws = this.bodyDraws;
    draws.length = 0;
    this.moonScreen = null;
    this.bodyScreen.length = 0;
    const pxPerRad = cam.pxPerRad;
    const set = settings.peek();
    // Draw far to near so the Moon covers planets.
    const order = [...this.bodies].sort((a, b) => b.distAu - a.distAu);
    for (const b of order) {
      const enu = this.frame.toEnu(b.dir);
      const app = refractEnu(enu);
      const s = cam.project(app, { x: 0, y: 0 }, true);
      const trueR = ((b.angDiamDeg / 2) * DEG * pxPerRad);
      let rCss = trueR;
      if (b.id === 'Moon') rCss = Math.max(trueR, 9);
      if (b.id === 'Sun') rCss = Math.max(trueR, 10);
      // Naked eye: a planet shows only where the eye could see it, as a star would (the Sun and Moon always).
      const seen = !set.nakedEye || b.id === 'Sun' || b.id === 'Moon' || eyeSeesAt(b.mag, enu[0], enu[1], enu[2], this.ctx.eyeSky, set.seeThroughGround);
      this.bodyScreen.push({ id: b.id, x: s?.x ?? -1e4, y: s?.y ?? -1e4, r: Math.max(rCss, 4), visible: !!s && cam.onScreen(s, 60), seen, enu });
      if (!s || !cam.onScreen(s, 200) || !seen) continue;
      const camDir = mat3Apply(cam.enuToCam, app[0], app[1], app[2]);
      const basis = this.localBasis(camDir);
      const light = this.toLocal(b.toSun, basis);
      const pole = b.pole ? this.toLocal(b.pole, basis) : ([0, 1, 0] as [number, number, number]);
      // Atmospheric colour and dimming.
      const alt = Math.asin(clamp(enu[2], -1, 1));
      // Seen through the ground, a body below the horizon is shown without the air's dimming.
      const T = directTransmittance(alt < 0 && set.seeThroughGround ? Math.PI / 2 : Math.max(alt, 0.004));
      const tint = BODY_COLOR[b.id as keyof typeof BODY_COLOR];
      const color: [number, number, number] = [
        tint[0] * Math.min(1.2, T[0] / T_ZENITH[0]),
        tint[1] * Math.min(1.2, T[1] / T_ZENITH[1]),
        tint[2] * Math.min(1.2, T[2] / T_ZENITH[2]),
      ];
      const radiusPx = rCss * dpr;
      const cx = s.x * dpr, cy = (cam.height - s.y) * dpr;
      if (b.id === 'Sun') {
        draws.push({ kind: 0, centerX: cx, centerY: cy, radiusPx, halfPx: radiusPx * 6, light, pole, color, bright: 2.2, glow: 1.4, glowSize: 1.6, starLike: 0, earthshine: 0 });
        continue;
      }
      if (b.id === 'Moon') {
        // Close up, swap in the 4k map and the relief map (once; they stay after that).
        if (rCss > 110 && !this.moonDetail && Date.now() > this.moonDetailRetry) {
          this.moonDetail = true;
          const base = import.meta.env.BASE_URL;
          Promise.all([loadTexture(this.gl, `${base}tex/moon-4k.webp`, { repeatS: true }), loadTexture(this.gl, `${base}tex/moon-normal-2k.webp`, { repeatS: true })])
            .then(([c, nrm]) => {
              this.bodyLayer.moonTex = c;
              this.bodyLayer.moonNormal = nrm;
            })
            .catch((e) => {
              console.warn(e);
              this.moonDetail = false;
              this.moonDetailRetry = Date.now() + 60000;
            });
        }
        const localToBody = this.moonMatrix(b, pole);
        this.moonScreen = { x: s.x, y: s.y, r: rCss, toBody: localToBody, light };
        const earthshine = 0.05 * Math.pow(1 - b.phaseFraction, 2) * darkness;
        const ec = this.eclipse;
        let shadow: [number, number, number, number] | undefined;
        if (ec.lunar) {
          const o = this.toLocal(ec.lunar.offsetDir, basis);
          const l = Math.hypot(o[0], o[1]) || 1;
          const k = ec.lunar.sep / ec.lunar.moonR;
          shadow = [(o[0] / l) * k, (o[1] / l) * k, ec.lunar.umbra / ec.lunar.moonR, ec.lunar.penumbra / ec.lunar.moonR];
        }
        draws.push({
          kind: 1, centerX: cx, centerY: cy, radiusPx, halfPx: radiusPx * 3.2, light, pole, color,
          bright: 1.05, glow: 0.35 * b.phaseFraction * ec.moonLight, glowSize: 1.2, starLike: 0, earthshine, localToBody, shadow,
        });
        continue;
      }
      // Planets: star-like until the disc is resolved.
      const rel = magLimit - b.mag;
      const starPx = clamp(1.6 * Math.pow(Math.pow(10, 0.4 * rel), 0.2) * 2.4 * dpr, 5 * dpr, 30 * dpr);
      const resolved = smoothstep(1.5 * dpr, 4 * dpr, radiusPx);
      const kind = b.id === 'Saturn' ? 3 : b.id === 'Jupiter' ? 4 : 2;
      const half = Math.max(starPx, radiusPx * (kind === 3 ? 2.6 : 1.6));
      const brightness = clamp(0.45 + rel * 0.08, 0.35, 1.2);
      // Close enough to show a surface: Mars (and Mercury and Venus) get their maps, turned the right way.
      const mapped = kind === 2 && (b.id === 'Mars' || b.id === 'Mercury' || b.id === 'Venus') && radiusPx > 6 * dpr;
      if (mapped) this.ensurePlanetTex(b.id);
      draws.push({
        kind, centerX: cx, centerY: cy, radiusPx: resolved > 0 ? radiusPx : half, halfPx: half, light, pole,
        color: [color[0] * brightness, color[1] * brightness, color[2] * brightness],
        bright: 1.1, glow: resolved * (radiusPx > 12 * dpr ? 0.05 : 0.18), glowSize: 0.6, starLike: 1 - resolved, earthshine: 0,
        ...(mapped && this.bodyLayer.planetTex.has(b.id) ? { texKey: b.id, localToBody: this.moonMatrix(b, pole) } : {}),
      });
    }
    this.bodyLayer.draw([W, H], draws);
  }

  private planetTexLoading = new Set<string>();
  /** Load a planet's surface map the first time its disc is big enough to show it. */
  private ensurePlanetTex(id: string) {
    if (this.bodyLayer.planetTex.has(id) || this.planetTexLoading.has(id)) return;
    this.planetTexLoading.add(id);
    const url = `${import.meta.env.BASE_URL}tex/planets/${id.toLowerCase()}.webp`;
    const img = new Image();
    img.onload = () => {
      // Average linear luminance, so the map adds detail without changing the planet's overall colour.
      const c = document.createElement('canvas');
      c.width = 64;
      c.height = 32;
      const g = c.getContext('2d')!;
      g.drawImage(img, 0, 0, 64, 32);
      const px = g.getImageData(0, 0, 64, 32).data;
      let sum = 0;
      for (let i = 0; i < px.length; i += 4) sum += 0.2126 * (px[i] / 255) ** 2.2 + 0.7152 * (px[i + 1] / 255) ** 2.2 + 0.0722 * (px[i + 2] / 255) ** 2.2;
      void loadTexture(this.gl, url, { repeatS: true }).then((tex) => this.bodyLayer.planetTex.set(id, { tex, avg: sum / (px.length / 4) }));
    };
    img.onerror = () => setTimeout(() => this.planetTexLoading.delete(id), 60000);
    img.src = url;
  }

  /** Rotation from a body's local view frame to its own axes (Moon, and planets with maps). */
  private moonMatrix(b: BodyState, pole: [number, number, number]): Float32Array {
    const lat = (b.libLat ?? 0) * DEG, lon = (b.libLon ?? 0) * DEG;
    // Body frame: e1 = pole, e2 = sub-observer direction made orthogonal, e3 = e1 × e2.
    const vObs: Vec3 = [Math.cos(lat) * Math.cos(lon), Math.cos(lat) * Math.sin(lon), Math.sin(lat)];
    const e1: Vec3 = [0, 0, 1];
    const e2 = norm([vObs[0], vObs[1], 0]);
    const e3 = cross(e1, e2);
    const f1 = norm(pole as Vec3);
    const zv: Vec3 = [0, 0, 1];
    const f2 = norm([zv[0] - f1[2] * f1[0], zv[1] - f1[2] * f1[1], zv[2] - f1[2] * f1[2]]);
    const f3 = cross(f1, f2);
    // body = E · Fᵀ · local, with E = [e1 e2 e3] and F = [f1 f2 f3] as columns.
    const E = [e1, e2, e3], F = [f1, f2, f3];
    const m = new Float32Array(9);
    for (let r = 0; r < 3; r++)
      for (let c = 0; c < 3; c++) {
        let s = 0;
        for (let k = 0; k < 3; k++) s += E[k][r] * F[k][c];
        m[r * 3 + c] = s;
      }
    // Program.m3 uploads row-major; the shader multiplies body = M · n.
    return m;
  }

  // ---------- 2D overlay ----------
  private drawOverlay(magLimit: number, darkness: number) {
    const g = this.g2d;
    const cam = this.camera;
    const set = settings.peek();
    const dpr = this.dpr2D;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cam.width, cam.height);
    const labels: LabelCandidate[] = [];
    const tmp = { x: 0, y: 0 };
    const fov = cam.fovDeg;

    // Cardinal points on the ground just under the horizon, where they read the same by day and by night
    // (on the skyline itself they would straddle its brightest sky and the dark hills).
    const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    for (let i = 0; i < 8; i++) {
      const p = cam.project(altAzToEnu(0, i * 45), { x: 0, y: 0 }, true);
      const q = p && cam.project(altAzToEnu(-2, i * 45), { x: 0, y: 0 }, true);
      if (!p || !q) continue;
      const d = Math.hypot(q.x - p.x, q.y - p.y) || 1;
      const at = { x: p.x + ((q.x - p.x) / d) * 11, y: p.y + ((q.y - p.y) / d) * 11 };
      if (cam.onScreen(at, -8)) labels.push({ ...at, r: 0, text: names[i], style: i === 0 ? 'cardinalN' : 'cardinal', priority: 5000, centered: true });
    }

    // Bodies.
    for (const b of this.bodyScreen) {
      if (!b.visible) continue;
      const st = this.bodies.find((x) => x.id === b.id)!;
      // Naked eye names the planets the eye can see (and one that is chosen, wherever it is).
      if (!b.seen && !(selection.peek()?.kind === 'body' && (selection.peek() as { id: string }).id === b.id)) continue;
      // Uranus and Neptune are only named when they could actually be seen (or when zoomed in on them).
      if ((b.id === 'Uranus' || b.id === 'Neptune') && st.mag > magLimit + 0.3 && fov > 15 && !(selection.peek()?.kind === 'body' && (selection.peek() as { id: string }).id === b.id)) continue;
      const style = b.id === 'Moon' ? 'moon' : b.id === 'Sun' ? 'sun' : 'planet';
      const sub = b.id === 'Moon' ? (this.eclipse.moonLight < 0.6 ? 'in Earth’s shadow' : this.isCrescentDay() ? undefined : `${Math.round(st.phaseFraction * 100)}% lit`) : b.id === 'Sun' && this.eclipse.sunLight < 0.97 ? `eclipsed ${Math.round((1 - this.eclipse.sunLight) * 100)}%` : undefined;
      labels.push({ x: b.x, y: b.y, r: b.r, text: b.id, sub, style, priority: 3000, sel: { kind: 'body', id: b.id } });
    }

    // Named stars: density depends on zoom.
    const arabic = set.skyCulture === 'arabic' ? this.arabic : null;
    const cul = isSharedCulture(set.skyCulture) && this.culture?.data.id === set.skyCulture ? this.culture : null;
    // Naked eye names every star the eye can see, whether or not star names are on (they are Full sky's choice).
    const budget = nameBudget(cam.width, cam.height - this.safe.top - this.safe.bottom - 164, labelScale.peek());
    if (set.nakedEye) this.eyeStarNames(labels, this.ctx.eyeSky, arabic, cul, budget);
    else if (set.starNames && cul && cul.starNames.size) {
      const nameLimit = Math.min(magLimit - 0.4, clamp(1.6 + 1.75 * Math.log2(90 / fov), 1.3, 6));
      const cat = this.stars;
      for (const [i, n] of cul.starNames) {
        if (cat.mag[i] > nameLimit) continue;
        const e = this.frame.toEnu([cat.dir[i * 3], cat.dir[i * 3 + 1], cat.dir[i * 3 + 2]]);
        if (e[2] < -0.01 && !set.seeThroughGround) continue;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -10)) continue;
        const text = n.en || n.native || n.pron || '';
        const sub = fov < 45 ? (cul.data.showNative && n.native && n.native !== text ? n.native : n.pron && n.pron !== text ? n.pron : undefined) : undefined;
        labels.push({ x: p.x, y: p.y, r: 4, text, sub, style: 'cultureStar', priority: 1000 - cat.mag[i] * 50, sel: { kind: 'star', index: i } });
      }
    } else if (set.starNames && cul) {
      /* this culture names no stars */
    } else if (set.starNames && arabic) {
      const nameLimit = Math.min(magLimit - 0.4, clamp(1.6 + 1.75 * Math.log2(90 / fov), 1.3, 6));
      const cat = this.stars;
      for (const [i, names] of arabic.starNames) {
        if (cat.mag[i] > nameLimit) continue;
        const e = this.frame.toEnu([cat.dir[i * 3], cat.dir[i * 3 + 1], cat.dir[i * 3 + 2]]);
        if (e[2] < -0.01 && !set.seeThroughGround) continue;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -10)) continue;
        labels.push({ x: p.x, y: p.y, r: 4, text: names[0].ar, sub: fov < 45 ? names[0].tr : undefined, style: 'arabicStar', priority: 1000 - cat.mag[i] * 50, sel: { kind: 'star', index: i } });
      }
    } else if (set.starNames && !isSharedCulture(set.skyCulture)) {
      const nameLimit = Math.min(magLimit - 0.4, clamp(1.6 + 1.75 * Math.log2(90 / fov), 1.3, 6));
      const cat = this.stars;
      for (const i of cat.named) {
        if (cat.mag[i] > nameLimit) break;
        const e = this.frame.toEnu([cat.dir[i * 3], cat.dir[i * 3 + 1], cat.dir[i * 3 + 2]]);
        if (e[2] < -0.01 && !set.seeThroughGround) continue;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -10)) continue;
        labels.push({ x: p.x, y: p.y, r: 4, text: starName(cat, i), style: 'star', priority: 1000 - cat.mag[i] * 50, sel: { kind: 'star', index: i } });
      }
    }

    // Arabic figures: the brightest ones when zoomed out, all of them closer in.
    if (set.constellationNames && arabic) {
      const lim = fov > 100 ? 1.6 : fov > 60 ? 2.6 : fov > 30 ? 3.8 : 9;
      const selA = selection.peek();
      arabic.sky.figures.forEach((f, k) => {
        const chosen = selA?.kind === 'arabic' && selA.index === k;
        if (arabic.minMag[k] > lim && !chosen) return;
        const e = this.frame.toEnu(arabic.geo.anchors[k]);
        if (e[2] < 0.02 && !set.seeThroughGround) return;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -20)) return;
        const pri = (f.kind === 'constellation' ? 230 : 190) - arabic.minMag[k] * 25 + (chosen ? 3000 : 0);
        labels.push({ x: p.x, y: p.y, r: 0, text: f.ar, sub: fov < 70 || chosen ? f.tr : undefined, style: 'arabic', priority: pri, centered: true, sel: { kind: 'arabic', index: k } });
      });
    }

    // Figures of another sky culture: the brightest when zoomed out, all of them closer in.
    if (set.constellationNames && cul) {
      const lim = fov > 100 ? 1.8 : fov > 60 ? 2.8 : fov > 30 ? 4 : 9;
      const selF = selection.peek();
      cul.data.figures.forEach((f, k) => {
        const chosen = selF?.kind === 'figure' && selF.index === k;
        if ((cul.minMag[k] > lim && !chosen) || !cul.geo.ranges[k]?.count) return;
        const e = this.frame.toEnu(cul.geo.anchors[k]);
        if (e[2] < 0.02 && !set.seeThroughGround) return;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -20)) return;
        const sub = fov < 70 || chosen ? (cul.data.showNative && f.native ? f.native : f.pron || undefined) : undefined;
        labels.push({ x: p.x, y: p.y, r: 0, text: f.en, sub, style: 'culture', priority: 220 - cul.minMag[k] * 25 + (chosen ? 3000 : 0), centered: true, sel: { kind: 'figure', index: k } });
      });
    }

    // Lunar stations along the ecliptic.
    if (set.lunarStations && this.arabic) {
      const st = this.arabic;
      st.stations.forEach((v, k) => {
        const e = this.frame.toEnu(v);
        if (e[2] < 0.0 && !set.seeThroughGround) return;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -20)) return;
        const n = st.sky.lunar.names[k];
        labels.push({ x: p.x, y: p.y, r: 0, text: n.ar, sub: fov < 90 ? `${k + 1} · ${n.tr}` : `${k + 1}`, style: 'station', priority: 170, centered: true, sel: { kind: 'manzil', index: k } });
      });
    }

    // Constellation names.
    if (set.constellationNames && !arabic && !cul) {
      this.cons.constellations.forEach((c, k) => {
        if (c.rank > (fov > 100 ? 1 : fov > 60 ? 2 : 3)) return;
        const e = this.frame.toEnu(this.consGeo.anchors[k]);
        if (e[2] < 0.02 && !set.seeThroughGround) return;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -20)) return;
        const text = set.arabicNames && c.ar ? c.ar : c.name;
        labels.push({ x: p.x, y: p.y, r: 0, text, style: 'constellation', priority: 200 - c.rank * 20, centered: true, sel: { kind: 'constellation', index: k } });
      });
    }

    // Deep-sky objects: in Naked eye, the few the eye can see.
    if (set.nakedEye) this.eyeDeepSky(g, labels, darkness);
    else if (set.deepSky && fov < 100) {
      g.strokeStyle = `rgba(170,214,200,${0.22 + 0.25 * darkness})`;
      g.lineWidth = 1;
      this.dsos.forEach((d, k) => {
        const lim = fov < 30 ? magLimit + 2.5 : fov < 60 ? magLimit + 1 : Math.min(magLimit, 5.5);
        if ((d.mag ?? 9) > lim || (!d.m && fov > 40)) return;
        const e = this.frame.toEnu(d.dir);
        if (e[2] < 0 && !set.seeThroughGround) return;
        const p = cam.project(e, tmp);
        if (!p || !cam.onScreen(p, -10)) return;
        const rpx = this.outlineDso(g, d, p);
        if (fov < 75) labels.push({ x: p.x, y: p.y, r: Math.min(rpx, 30), text: d.m ? `M${d.m}` : d.cat, sub: fov < 30 ? d.name : undefined, style: 'dso', priority: 400 - (d.mag ?? 9) * 20, sel: { kind: 'dso', index: k } });
      });
    }

    // Jupiter's four big moons once the planet is magnified (not through the ground), but not in Naked eye: no eye sees them.
    if (fov < 4 && !set.nakedEye) {
      const jup = this.bodies.find((b) => b.id === 'Jupiter');
      if (jup && (set.seeThroughGround || this.frame.toEnu(jup.dir)[2] > -0.005)) {
        const jm = A.JupiterMoons(this.ctx.date);
        const moons: [string, A.StateVector][] = [['Io', jm.io], ['Europa', jm.europa], ['Ganymede', jm.ganymede], ['Callisto', jm.callisto]];
        for (const [name, v] of moons) {
          const e = this.frame.toEnu(norm([jup.pos[0] + v.x, jup.pos[1] + v.y, jup.pos[2] + v.z]));
          const p = cam.project(e, tmp);
          if (!p || !cam.onScreen(p, 10)) continue;
          g.fillStyle = 'rgba(255,248,235,0.95)';
          g.shadowColor = 'rgba(255,248,235,0.8)';
          g.shadowBlur = 4;
          g.beginPath();
          g.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
          g.fill();
          g.shadowBlur = 0;
          labels.push({ x: p.x, y: p.y, r: 3, text: name, style: 'star', priority: 2800 });
        }
      }
    }

    for (const p of this.providers) {
      p.overlay?.(g, this.ctx);
      p.labels?.(this.ctx, labels);
    }

    // Moon features once the Moon is big on screen.
    const ms = this.moonScreen;
    if (ms && ms.r > 70) {
      const m = ms.toBody; // body = M · local, so local = Mᵀ · body
      for (const f of MOON_FEATURES) {
        const la = f.lat * DEG, lo = f.lon * DEG;
        const b: Vec3 = [Math.cos(la) * Math.cos(lo), Math.cos(la) * Math.sin(lo), Math.sin(la)];
        const lx = m[0] * b[0] + m[3] * b[1] + m[6] * b[2];
        const ly = m[1] * b[0] + m[4] * b[1] + m[7] * b[2];
        const lz = m[2] * b[0] + m[5] * b[1] + m[8] * b[2];
        if (lz < 0.25) continue;
        const lit = lx * ms.light[0] + ly * ms.light[1] + lz * ms.light[2];
        const x = ms.x + lx * ms.r, y = ms.y - ly * ms.r;
        g.fillStyle = 'rgba(255,255,255,0.8)';
        g.beginPath();
        g.arc(x, y, 1.6, 0, Math.PI * 2);
        g.fill();
        labels.push({ x, y, r: 3, text: f.name, sub: fov < 0.6 ? f.note : undefined, style: 'dso', priority: 2700, dim: lit < 0.03 });
      }
    }

    // The selection's focus ring (drawn further down): its own label moves out beyond it.
    const selNow = selection.peek();
    const selLoc = selNow ? this.locate(selNow) : null;
    const ring = selNow && selLoc ? this.ringAt(selNow, selLoc.enu) : null;
    // Below the horizon, things are named only through a see-through ground (fainter, in italics), or when selected.
    let kept = 0;
    for (const L of labels) {
      const chosen = !!selNow && !!L.sel && sameSel(L.sel, selNow);
      if (L.style !== 'cardinal' && L.style !== 'cardinalN' && cam.unproject(L.x, L.y)[2] < 0) {
        if (!set.seeThroughGround && !chosen) continue;
        L.below = true;
      }
      if (chosen && ring) L.r = Math.max(L.r, ring.r + 2);
      labels[kept++] = L;
    }
    labels.length = kept;

    // The selected object's path through the night, with hour marks. Not the Moon's on the 29th, the 30th or the 1st,
    // when nothing shows its rising or setting (src/live/saudi.ts).
    const keepClear: { x0: number; y0: number; x1: number; y1: number }[] = [];
    const moonSel = selNow?.kind === 'body' && selNow.id === 'Moon';
    if (selNow && viewMode.peek() === 'manual' && !huntReticle.peek() && ['star', 'body', 'dso', 'small'].includes(selNow.kind) && !(moonSel && (fov < 5 || this.isCrescentDay()))) {
      const path = this.nightPath(selNow);
      if (path.length > 1) {
        const pts: { x: number; y: number; t: number }[] = [];
        for (const p of path) {
          if (p.enu[2] < -0.02) continue;
          const q = cam.project(p.enu, { x: 0, y: 0 });
          if (q) pts.push({ x: q.x, y: q.y, t: p.t });
        }
        g.save();
        g.strokeStyle = 'rgba(255,214,140,0.45)';
        g.setLineDash([2, 5]);
        g.lineWidth = 1.4;
        g.beginPath();
        pts.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
        g.stroke();
        g.setLineDash([]);
        const ts = labelScale.peek();
        g.font = `600 ${10 * ts}px ${FAMILY}`;
        g.textAlign = 'center';
        g.textBaseline = 'middle';
        const hhmm = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: place.peek().timeZone });
        for (const p of pts) {
          // Hour marks, except the one sitting on the object right now (it would cover its marker) and any
          // under its ring; light or dark like the labels, which keep clear of them.
          if (new Date(p.t).getUTCMinutes() !== 0 || !cam.onScreen(p, -10) || Math.abs(p.t - this.frame.date.getTime()) < 20 * 60e3) continue;
          const box = { x0: p.x - 17 * ts, y0: p.y - 14 * ts, x1: p.x + 17 * ts, y1: p.y + 3 };
          if (ring && box.x0 < ring.x + ring.r + 3 && box.x1 > ring.x - ring.r - 3 && box.y0 < ring.y + ring.r + 3 && box.y1 > ring.y - ring.r - 3) continue;
          const text = hhmm.format(p.t);
          const k = tone(`hour ${text}`, Math.min(this.sky.luminance(cam.unproject(p.x, p.y)), this.sky.luminance(cam.unproject(p.x, p.y - 7))), this.clock, !!reducedMotion?.matches);
          drawTag(g, text, p.x, p.y - 7 * ts, p, k, { light: 'rgba(255,214,140,0.9)', dark: 'rgba(58,34,3,0.95)' }, this.dpr2D);
          keepClear.push(box);
        }
        g.textAlign = 'start';
        g.restore();
      }
    }

    // Selection ring or edge arrow.
    if (selNow && selLoc) this.drawSelectionMarker(g, selLoc.enu, ring, selKey(selNow));

    // The evening of the 29th, pointing at the sky: a frame on the horizon where the Moon is, or an arrow towards it.
    if (crescentFrame.peek() && viewMode.peek() === 'ar') this.drawCrescentFrame(g);

    // Binocular or telescope field of view, centred, to show what fits in the eyepiece.
    const EYEPIECES = { bino7: [7.1, '7×50 binoculars'], bino10: [5.2, '10×50 binoculars'], scope: [1.0, 'Small telescope, low power'] } as const;
    if (set.eyepiece !== 'none') {
      const [field, name] = EYEPIECES[set.eyepiece];
      const half = Math.min(cam.width, cam.height) / 2;
      const r = cam.projection === 'perspective'
        ? (half * Math.tan((field / 2) * DEG)) / Math.tan((fov / 2) * DEG)
        : (half * Math.tan((field / 4) * DEG)) / Math.tan((fov / 4) * DEG);
      if (r > 12 && r < Math.hypot(cam.width, cam.height)) {
        g.save();
        g.strokeStyle = 'rgba(255,214,140,0.55)';
        g.lineWidth = 1.5;
        g.setLineDash([6, 5]);
        g.beginPath();
        g.arc(cam.width / 2, cam.height / 2, r, 0, Math.PI * 2);
        g.stroke();
        g.setLineDash([]);
        g.font = `600 ${11 * labelScale.peek()}px ${FAMILY}`;
        g.fillStyle = 'rgba(255,214,140,0.8)';
        g.textAlign = 'center';
        g.fillText(`${name} · ${field}°`, cam.width / 2, cam.height / 2 - r - 7);
        g.restore();
      }
    }

    // AR reticle (and during Sky Hunt, in either mode); it stands out in Sky Hunt and while lining up with the sky.
    if (viewMode.peek() === 'ar' || huntReticle.peek()) {
      const strong = huntReticle.peek() || alignReticle.peek();
      g.strokeStyle = strong ? 'rgba(255,214,140,0.75)' : 'rgba(255,255,255,0.35)';
      g.lineWidth = strong ? 2 : 1.2;
      g.beginPath();
      g.arc(cam.width / 2, cam.height / 2, strong ? 24 : 18, 0, Math.PI * 2);
      g.stroke();
      if (viewMode.peek() === 'ar') this.updatePointing();
    }

    // Keep labels inside the safe area, clear of the top bar, the dock and the selection ring.
    const s = this.safe;
    const reserved = [
      { x0: 0, y0: 0, x1: cam.width, y1: s.top + 60 },
      { x0: 0, y0: cam.height - s.bottom - 104, x1: cam.width, y1: cam.height },
    ];
    // Banners (another place, another kind of light) stack below the top bar.
    const rows = (previewFrom.peek() ? 1 : 0) + (skyBand.peek() ? 1 : 0);
    // (They sit on the left when cards open at the side: wide screens and landscape phones.)
    const side = cam.width >= 900 || (cam.width > cam.height && cam.height <= 560);
    const bannerY = { y0: s.top + 50, y1: s.top + 58 + 44 * rows };
    if (rows) reserved.push(side ? { x0: 0, x1: s.left + 356, ...bannerY } : { x0: cam.width / 2 - 160, x1: cam.width / 2 + 160, ...bannerY });
    // A ring round a big disc (the Moon close up) leaves the names on the disc alone.
    if (ring && ring.r <= 40) reserved.push({ x0: ring.x - ring.r - 3, y0: ring.y - ring.r - 3, x1: ring.x + ring.r + 3, y1: ring.y + ring.r + 3 });
    reserved.push(...keepClear);
    const sky = this.sky;
    this.labelHits = hideLabels.peek() ? [] : drawLabels(g, labels, cam.width, cam.height, reserved, {
      skyLum: (x, y) => sky.luminance(cam.unproject(x, y)),
      bounds: { x0: s.left + 2, y0: s.top + 2, x1: cam.width - s.right - 2, y1: cam.height - s.bottom - 2 },
      dpr: this.dpr2D,
      time: this.clock,
      still: !!reducedMotion?.matches,
      scale: labelScale.peek(),
      budget: set.nakedEye ? budget : undefined,
      dots: set.nakedEye ? this.eyeDots : undefined,
    });
  }

  // ---------- Naked eye ----------
  /** Close doubles, to name each once (nakedeye.ts), worked out the first time Naked eye needs them. */
  private twins: Uint8Array | null = null;
  /** The deep-sky objects an eye can see (nakedeye.ts, DSO_EYE): index and the limit each needs. */
  private eyeDsoList: { k: number; mag: number }[] | null = null;
  private eyePt = { x: 0, y: 0 };
  /** Where the stars Naked eye names are on screen, so other names leave them in sight. */
  private eyeDots: { x0: number; y0: number; x1: number; y1: number }[] = [];

  /**
   * Naked eye's star names: every star the eye can see is named, brightest first, as many as the label budget leaves
   * room for (nameBudget: zooming in spreads the stars apart and names fainter ones), and a chosen star, and the one in
   * the pointing circle, always. Nothing is allocated for a star until it is one of the few named.
   */
  private eyeStarNames(labels: LabelCandidate[], sky: EyeSky, arabic: SkyEngine['arabic'], cul: SkyEngine['culture'], budget: number) {
    const cam = this.camera, cat = this.stars, M = this.frame.eqjToEnu, d = cat.dir;
    const below = settings.peek().seeThroughGround;
    const twins = (this.twins ??= closeCompanions(cat.dir, cat.mag, cat.count));
    const fov = cam.fovDeg;
    // Only stars in the view's circle are projected (with a little to spare for refraction and the margins).
    const aspect = Math.max(cam.width, cam.height) / Math.min(cam.width, cam.height);
    const half = (fov * DEG) / 2;
    const reach = (cam.projection === 'perspective' ? Math.atan(Math.tan(half) * Math.hypot(1, aspect)) : 2 * Math.atan(Math.tan(half / 2) * Math.hypot(1, aspect))) + 0.05;
    const cosReach = reach >= Math.PI ? -2 : Math.cos(reach);
    const f = cam.forward();
    const sel = selection.peek();
    const selStar = sel?.kind === 'star' ? sel.index : -1;
    const pt = viewMode.peek() === 'ar' ? pointingAt.peek() : null;
    const ptStar = pt?.sel.kind === 'star' ? pt.sel.index : -1;
    // Candidates for the budget's names: the brightest in view, with room for those that will not fit.
    const cap = 4 * budget;
    const p = this.eyePt;
    // Names leave the Sun, Moon and planets in sight, as they do the stars they name.
    const dots = (this.eyeDots = this.bodyScreen.filter((b) => b.visible && b.seen).map((b) => ({ x0: b.x - b.r, y0: b.y - b.r, x1: b.x + b.r, y1: b.y + b.r })));
    let n = 0, named = false;
    for (let i = 0; i < cat.count; i++) {
      const m = cat.mag[i];
      if (m > sky.max) break; // brightest first, and none fainter than the limit anywhere on the sky shows
      const x = d[i * 3], y = d[i * 3 + 1], z = d[i * 3 + 2];
      const ez = M[6] * x + M[7] * y + M[8] * z;
      if (seenMag(m, ez, below) > sky.max) continue;
      const special = i === selStar || i === ptStar;
      if (!special && (n >= cap || twins[i])) continue;
      const ex = M[0] * x + M[1] * y + M[2] * z, ey = M[3] * x + M[4] * y + M[5] * z;
      if (ex * f[0] + ey * f[1] + ez * f[2] < cosReach) continue;
      // Seen where it is, against the sky's glow there: only for the few in view.
      if (!eyeSeesAt(m, ex, ey, ez, sky, below)) continue;
      if (!cam.projectXYZ(ex, ey, ez, p) || !cam.onScreen(p)) continue;
      const L = this.starLabel(i, p.x, p.y, arabic, cul, fov);
      if (!L) continue;
      dots.push({ x0: p.x - 3, y0: p.y - 3, x1: p.x + 3, y1: p.y + 3 });
      if (i === selStar) {
        L.priority = 3100;
        named = true;
      } else if (i === ptStar) L.priority = 2900;
      else {
        L.counted = true;
        n++;
      }
      labels.push(L);
    }
    // A chosen star that is too faint to see tonight (or below the horizon) is named all the same, beside its ring.
    if (selStar >= 0 && !named) {
      const q = cam.project(this.frame.toEnu(this.starDir(selStar)), p);
      const L = q && cam.onScreen(q) ? this.starLabel(selStar, q.x, q.y, arabic, cul, fov) : null;
      if (L) labels.push({ ...L, priority: 3100 });
    }
  }

  /**
   * A star's name on the sky: as the chosen sky culture names it, else its proper name, else its Bayer or Flamsteed
   * designation ("δ Cyg", "61 Cyg"). One known only by a catalogue number comes after all of those; one with no name
   * at all (a part of a double listed on its own) gets none.
   */
  private starLabel(i: number, x: number, y: number, arabic: SkyEngine['arabic'], cul: SkyEngine['culture'], fov: number): LabelCandidate | null {
    const cat = this.stars;
    const sel: Selection = { kind: 'star', index: i };
    const priority = 1000 - cat.mag[i] * 50;
    const an = arabic?.starNames.get(i);
    if (an) return { x, y, r: 4, text: an[0].ar, sub: fov < 45 ? an[0].tr : undefined, style: 'arabicStar', priority, sel };
    const cn = cul?.starNames.get(i);
    const ct = cn && (cn.en || cn.native || cn.pron);
    if (cul && cn && ct) {
      const sub = fov < 45 ? (cul.data.showNative && cn.native && cn.native !== ct ? cn.native : cn.pron && cn.pron !== ct ? cn.pron : undefined) : undefined;
      return { x, y, r: 4, text: ct, sub, style: 'cultureStar', priority, sel };
    }
    const text = starName(cat, i);
    if (text === 'Star') return null;
    const s = cat.info.get(i);
    return { x, y, r: 4, text, style: 'star', priority: s && (s.name || s.bayer || s.flam) ? priority : priority - 400, sel };
  }

  /** The deep-sky objects an eye can see (DSO_EYE), by index, found once. */
  private eyeDsos(): { k: number; mag: number }[] {
    return (this.eyeDsoList ??= this.dsos.flatMap((d, k) => (DSO_EYE[d.id] !== undefined ? [{ k, mag: DSO_EYE[d.id] }] : [])));
  }

  /** Whether the eye can see deep-sky object `k` tonight (only those on the naked-eye list ever are). */
  private eyeSeesDso(k: number): boolean {
    const m = DSO_EYE[this.dsos[k].id];
    if (m === undefined) return false;
    const e = this.frame.toEnu(this.dsos[k].dir);
    return eyeSeesAt(m, e[0], e[1], e[2], this.ctx.eyeSky, settings.peek().seeThroughGround);
  }

  /**
   * Naked eye's clusters, nebulae and galaxies: those on the naked-eye list the eye can see tonight, outlined and named
   * by their common names (Pleiades, Andromeda Galaxy), above every star's name. A chosen one is named whatever it is.
   */
  private eyeDeepSky(g: CanvasRenderingContext2D, labels: LabelCandidate[], darkness: number) {
    const cam = this.camera, fov = cam.fovDeg;
    const sel = selection.peek();
    const selD = sel?.kind === 'dso' ? sel.index : -1;
    const tmp = { x: 0, y: 0 };
    g.strokeStyle = `rgba(170,214,200,${0.22 + 0.25 * darkness})`;
    g.lineWidth = 1;
    const mark = (k: number, priority: number) => {
      const d = this.dsos[k];
      const p = cam.project(this.frame.toEnu(d.dir), tmp);
      if (!p || !cam.onScreen(p, -10)) return;
      const rpx = this.outlineDso(g, d, p);
      labels.push({ x: p.x, y: p.y, r: Math.min(rpx, 30), text: d.name || d.id, sub: d.name && fov < 40 ? d.id : undefined, style: 'dso', priority, sel: { kind: 'dso', index: k } });
    };
    for (const { k, mag } of this.eyeDsos()) if (k === selD || this.eyeSeesDso(k)) mark(k, k === selD ? 3100 : 1200 + (7 - mag) * 40);
    if (selD >= 0 && DSO_EYE[this.dsos[selD].id] === undefined) mark(selD, 3100);
  }

  /** A deep-sky object's outline on the sky (its size and shape, or a small circle); returns its radius in CSS px. */
  private outlineDso(g: CanvasRenderingContext2D, d: DeepSky, p: { x: number; y: number }): number {
    const rpx = Math.max(3.5, ((d.maj ?? 5) / 60 / 2) * DEG * this.camera.pxPerRad);
    if (rpx > 3.5 && rpx < 400) {
      g.beginPath();
      g.ellipse(p.x, p.y, rpx, rpx * Math.max(0.35, (d.min ?? d.maj ?? 5) / (d.maj ?? 5)), 0, 0, Math.PI * 2);
      g.stroke();
    } else {
      g.beginPath();
      g.arc(p.x, p.y, 3.5, 0, Math.PI * 2);
      g.stroke();
    }
    return rpx;
  }

  /** Positions every 20 minutes from 2 h before to 10 h after the current time (cached). */
  private nightPath(sel: Selection): { t: number; enu: Vec3 }[] {
    const t0 = Math.floor(this.ctx.date.getTime() / 1200e3) * 1200e3;
    const pl = place.peek();
    const key = `${JSON.stringify(sel)}|${t0}|${pl.lat},${pl.lon}`;
    if (this.pathCache?.key === key) return this.pathCache.pts;
    const pts: { t: number; enu: Vec3 }[] = [];
    let fixed: Vec3 | null = null;
    if (sel.kind === 'star') fixed = this.starDir(sel.index);
    else if (sel.kind === 'dso') fixed = this.dsos[sel.index].dir;
    for (let t = t0 - 2 * 3600e3; t <= t0 + 10 * 3600e3; t += 1200e3) {
      const d = new Date(t);
      const M = eqjToEnuMatrix(d, pl);
      let v: Vec3 | null = fixed;
      if (!v && sel.kind === 'body') {
        const e = A.Equator(A.Body[sel.id as keyof typeof A.Body], d, this.frame.observer, false, true);
        v = norm([e.vec.x, e.vec.y, e.vec.z]);
      } else if (!v && sel.kind === 'small') {
        const loc = this.providers.map((p) => p.locate?.(sel, { ...this.ctx, date: d, frame: Object.assign(new SkyFrame(), { eqjToEnu: M, enuToEqj: M }) })).find(Boolean);
        if (loc) {
          pts.push({ t, enu: loc.enu });
          continue;
        }
      }
      if (v) pts.push({ t, enu: mat3Apply(M, v[0], v[1], v[2]) });
    }
    this.pathCache = { key, pts };
    return pts;
  }

  /** Centre and radius (CSS px) of the selection's ring while it is on screen, clear of a disc or deep-sky outline. */
  private ringAt(sel: Selection, enu: Vec3): { x: number; y: number; r: number } | null {
    const cam = this.camera;
    const p = cam.project(enu);
    if (!p || !cam.onScreen(p, -24)) return null;
    let size = 0;
    if (sel.kind === 'body') size = this.bodyScreen.find((b) => b.id === sel.id)?.r ?? 0;
    else if (sel.kind === 'dso') size = ((this.dsos[sel.index].maj ?? 5) / 120) * DEG * cam.pxPerRad;
    return { x: p.x, y: p.y, r: Math.max(16, size + 7) };
  }

  private markKey = '';
  private markSince = 0;
  /**
   * Focus-lock ring on the selection: a thin circle broken on the diagonals that closes in from 1.25× over
   * 280 ms, light on a dark sky and navy on a bright one. Off screen, an arrow at the edge points the way.
   */
  private drawSelectionMarker(g: CanvasRenderingContext2D, enu: Vec3, ring: { x: number; y: number; r: number } | null, key: string) {
    const cam = this.camera;
    if (key !== this.markKey) {
      this.markKey = key;
      this.markSince = this.clock;
    }
    if (ring) {
      const still = !!reducedMotion?.matches;
      const u = still ? 1 : Math.min(1, (this.clock - this.markSince) / 0.28);
      const r = ring.r * (1.25 - 0.25 * (1 - (1 - u) ** 3));
      let lo = 1;
      for (const [dx, dy] of [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r]]) lo = Math.min(lo, this.sky.luminance(cam.unproject(ring.x + dx, ring.y + dy)));
      const k = tone('selection ring', lo, this.clock, still);
      const gap = 0.13; // radians each side of 45°, 135°, 225° and 315°
      g.save();
      g.lineWidth = 1.5;
      g.shadowBlur = 2 * this.dpr2D;
      for (const dark of [false, true]) {
        const a = (dark ? k : 1 - k) * (still ? 1 : Math.min(1, u * 3));
        if (a <= 0.01) continue;
        g.globalAlpha = a;
        g.strokeStyle = dark ? 'rgba(10,24,52,0.9)' : 'rgba(255,255,255,0.9)';
        g.shadowColor = dark ? 'rgba(255,255,255,0.45)' : 'rgba(3,5,10,0.6)';
        for (let q = 0; q < 4; q++) {
          const c = (q * Math.PI) / 2;
          g.beginPath();
          g.arc(ring.x, ring.y, r, c - Math.PI / 4 + gap, c + Math.PI / 4 - gap);
          g.stroke();
        }
      }
      g.restore();
      return;
    }
    this.drawEdgeArrow(g, refractEnu(enu));
  }

  /** An arrow at the screen's edge pointing the shortest way towards `enu` (already refracted), off screen. */
  private drawEdgeArrow(g: CanvasRenderingContext2D, enu: Vec3) {
    const cam = this.camera;
    const c = mat3Apply(cam.enuToCam, ...enu);
    let ang = Math.atan2(-c[1], c[0]);
    if (!Number.isFinite(ang)) ang = 0;
    const cx = cam.width / 2, cy = cam.height / 2;
    const rx = cam.width / 2 - 34, ry = cam.height / 2 - 34;
    const k = Math.min(rx / Math.abs(Math.cos(ang) || 1e-6), ry / Math.abs(Math.sin(ang) || 1e-6));
    const x = cx + Math.cos(ang) * k, y = cy + Math.sin(ang) * k;
    g.save();
    g.translate(x, y);
    g.rotate(ang);
    g.fillStyle = 'rgba(255,214,140,0.95)';
    g.shadowColor = 'rgba(0,0,0,0.6)';
    g.shadowBlur = 6;
    g.beginPath();
    g.moveTo(14, 0);
    g.lineTo(-8, -10);
    g.lineTo(-3, 0);
    g.lineTo(-8, 10);
    g.closePath();
    g.fill();
    g.restore();
  }

  /**
   * Where to look for the new crescent on the evening of the 29th (BUILD-SPEC item 19): a frame on the horizon, 16° wide
   * and from just below it to 9° up, centred on the Moon's bearing, whether or not the crescent can be seen there.
   */
  private drawCrescentFrame(g: CanvasRenderingContext2D) {
    const cam = this.camera;
    const moon = this.locate({ kind: 'body', id: 'Moon' });
    if (!moon) return;
    const az = enuToAltAz(moon.enu).az;
    const half = 8, low = -1.5, high = 9;
    const edge: Vec3[] = [];
    for (let i = 0; i <= 8; i++) edge.push(altAzToEnu(low, az - half + (2 * half * i) / 8));
    for (let i = 1; i <= 4; i++) edge.push(altAzToEnu(low + ((high - low) * i) / 4, az + half));
    for (let i = 1; i <= 8; i++) edge.push(altAzToEnu(high, az + half - (2 * half * i) / 8));
    for (let i = 1; i < 4; i++) edge.push(altAzToEnu(high - ((high - low) * i) / 4, az - half));
    const pts = edge.map((v) => cam.project(v, { x: 0, y: 0 }, true));
    const middle = altAzToEnu((low + high) / 2, az);
    const centre = cam.project(middle, { x: 0, y: 0 }, true);
    if (!centre || !cam.onScreen(centre, -30) || pts.some((p) => !p)) return this.drawEdgeArrow(g, middle);
    g.save();
    g.lineWidth = 2;
    g.lineJoin = 'round';
    g.setLineDash([9, 6]);
    g.shadowColor = 'rgba(0,0,0,0.55)';
    g.shadowBlur = 4;
    g.strokeStyle = 'rgba(255,214,140,0.95)';
    g.beginPath();
    pts.forEach((p, i) => (i ? g.lineTo(p!.x, p!.y) : g.moveTo(p!.x, p!.y)));
    g.closePath();
    g.stroke();
    g.restore();
  }

  private updatePointing() {
    if (this.clock - this.lastPointingCheck < 0.25) return;
    this.lastPointingCheck = this.clock;
    const cam = this.camera;
    const cur = pointingAt.peek();
    // An iPhone still learning north holds the view still, so the middle of the screen is not where the phone points:
    // name nothing until it is.
    const hit = this.orientation.northKnown ? this.pick(cam.width / 2, cam.height / 2, 40) : null;
    if (!hit) {
      if (cur) pointingAt.value = null;
      return;
    }
    const label = this.labelFor(hit.sel);
    if (!cur || cur.label !== label) pointingAt.value = { label, sel: hit.sel };
  }

  // ---------- Lookup ----------
  labelFor(sel: Selection): string {
    switch (sel.kind) {
      case 'star':
        return starName(this.stars, sel.index);
      case 'body':
        return sel.id;
      case 'dso': {
        const d = this.dsos[sel.index];
        return d.name ? `${d.id} · ${d.name}` : d.id;
      }
      case 'constellation':
        return this.cons.constellations[sel.index].name;
      case 'arabic':
      case 'manzil':
      case 'figure':
      case 'simbad':
        return this.locate(sel)?.label ?? '';
      default:
        for (const p of this.providers) {
          const l = p.locate?.(sel, this.ctx);
          if (l) return l.label;
        }
        return '';
    }
  }

  /** ENU direction (true, unrefracted) for a selection. */
  locate(sel: Selection): { enu: Vec3; label: string } | null {
    switch (sel.kind) {
      case 'star': {
        const d = this.stars.dir, i = sel.index;
        return { enu: this.frame.toEnu([d[i * 3], d[i * 3 + 1], d[i * 3 + 2]]), label: starName(this.stars, i) };
      }
      case 'body': {
        const b = this.bodies.find((x) => x.id === sel.id);
        return b ? { enu: this.frame.toEnu(b.dir), label: b.id } : null;
      }
      case 'dso':
        return { enu: this.frame.toEnu(this.dsos[sel.index].dir), label: this.dsos[sel.index].id };
      case 'constellation':
        return { enu: this.frame.toEnu(this.consGeo.anchors[sel.index]), label: this.cons.constellations[sel.index].name };
      case 'arabic': {
        const a = this.arabic;
        return a && a.sky.figures[sel.index] ? { enu: this.frame.toEnu(a.geo.anchors[sel.index]), label: a.sky.figures[sel.index].tr } : null;
      }
      case 'manzil': {
        const a = this.arabic;
        return a && a.stations[sel.index] ? { enu: this.frame.toEnu(a.stations[sel.index]), label: a.sky.lunar.names[sel.index].tr } : null;
      }
      case 'simbad':
        return { enu: this.frame.toEnu(raDecToVec(sel.ra, sel.dec)), label: sel.name };
      case 'figure': {
        const c = this.culture;
        // A figure with no drawable points has no place in the sky.
        return c && c.data.figures[sel.index] && c.geo.ranges[sel.index]?.count ? { enu: this.frame.toEnu(c.geo.anchors[sel.index]), label: c.data.figures[sel.index].en } : null;
      }
      default:
        for (const p of this.providers) {
          const l = p.locate?.(sel, this.ctx);
          if (l) return l;
        }
        return null;
    }
  }

  /**
   * Find the object nearest a screen point (CSS px). Empty sky gives the constellation there only when
   * `constellations` is set; taps on the ground find nothing unless the ground is see-through. In Naked eye only what
   * the eye can see is found, so the pointing circle names that first.
   */
  pick(x: number, y: number, radius = 26, constellations = true): PickHit | null {
    if (!this.ready || !this.ctx) return null;
    const cam = this.camera;
    const { nakedEye, seeThroughGround } = settings.peek();
    const ground = !seeThroughGround && cam.unproject(x, y)[2] < -0.005;
    let best: PickHit | null = null;
    const consider = (h: PickHit | null) => {
      if (h && (!best || h.dist < best.dist)) best = h;
    };
    for (const b of this.bodyScreen) {
      if (!b.visible || !b.seen) continue;
      const d = Math.hypot(b.x - x, b.y - y) - b.r;
      if (d < radius) consider({ sel: { kind: 'body', id: b.id }, dist: Math.max(0, d) - 6 });
    }
    for (const p of this.providers) {
      const h = p.pick?.(this.ctx, x, y);
      if (h && h.dist < radius) consider({ sel: h.sel, dist: h.dist - 3 });
    }
    if (best) return best;
    if (ground) return null;
    // Names on the sky can be tapped.
    for (const h of this.labelHits) {
      if (x > h.x0 - 4 && x < h.x1 + 4 && y > h.y0 - 4 && y < h.y1 + 4) return { sel: h.sel, dist: 0 };
    }
    // Stars: favour bright ones near the tap.
    const dir = cam.unproject(x, y);
    const cat = this.stars;
    const lim = this.ctx.magLimit;
    const sky = this.ctx.eyeSky;
    const cosR = Math.cos(((radius / cam.pxPerRad) * 1.5) || 0.05);
    const eqj = this.frame.toEqj(dir);
    const M = this.frame.eqjToEnu;
    let bestStar = -1, bestScore = Infinity;
    for (let i = 0; i < cat.count; i++) {
      if (cat.mag[i] > (nakedEye ? sky.max : lim)) break;
      const dd = cat.dir[i * 3] * eqj[0] + cat.dir[i * 3 + 1] * eqj[1] + cat.dir[i * 3 + 2] * eqj[2];
      if (dd < cosR) continue;
      // Naked eye: a star the eye can see tonight, a close double as its brighter part.
      if (nakedEye) {
        const x = cat.dir[i * 3], y = cat.dir[i * 3 + 1], z = cat.dir[i * 3 + 2];
        if (this.twins?.[i] || !eyeSeesAt(cat.mag[i], M[0] * x + M[1] * y + M[2] * z, M[3] * x + M[4] * y + M[5] * z, M[6] * x + M[7] * y + M[8] * z, sky, seeThroughGround)) continue;
      }
      const px = Math.acos(Math.min(1, dd)) * cam.pxPerRad;
      const score = px - (lim - cat.mag[i]) * 3;
      if (px < radius && score < bestScore) {
        bestScore = score;
        bestStar = i;
      }
    }
    if (bestStar >= 0) return { sel: { kind: 'star', index: bestStar }, dist: 0 };
    // Only what is on show can be tapped (layers hidden, or "show only" one kind of thing).
    const set = settings.peek();
    // Deep-sky objects.
    const tmp = { x: 0, y: 0 };
    let bestD = -1, bestDd = radius;
    this.dsos.forEach((d, k) => {
      if (nakedEye ? !this.eyeSeesDso(k) : !set.deepSky || (d.mag ?? 9) > lim + 2.5) return;
      const p = cam.project(this.frame.toEnu(d.dir), tmp);
      if (!p) return;
      const dd = Math.hypot(p.x - x, p.y - y);
      if (dd < bestDd) {
        bestDd = dd;
        bestD = k;
      }
    });
    if (bestD >= 0) return { sel: { kind: 'dso', index: bestD }, dist: bestDd };
    // In the Arabic sky or another culture, the figure whose lines pass nearest the finger.
    const culNow = set.skyCulture;
    const ar = culNow === 'arabic' ? this.arabic : isSharedCulture(culNow) && this.culture?.data.id === culNow ? this.culture : null;
    if (ar && set.constellationLines) {
      const seg = ar.geo.segments;
      let bestF = -1, bestFd = radius * 1.5;
      ar.geo.ranges.forEach((r, k) => {
        for (let q = r.first; q < r.first + r.count; q++) {
          const a = cam.project(this.frame.toEnu([seg[q * 6], seg[q * 6 + 1], seg[q * 6 + 2]]), { x: 0, y: 0 });
          const b = cam.project(this.frame.toEnu([seg[q * 6 + 3], seg[q * 6 + 4], seg[q * 6 + 5]]), { x: 0, y: 0 });
          if (!a || !b) continue;
          const dx = b.x - a.x, dy = b.y - a.y;
          const t = clamp(((x - a.x) * dx + (y - a.y) * dy) / (dx * dx + dy * dy || 1), 0, 1);
          const dd = Math.hypot(a.x + t * dx - x, a.y + t * dy - y);
          if (dd < bestFd) {
            bestFd = dd;
            bestF = k;
          }
        }
      });
      if (bestF >= 0) return { sel: culNow === 'arabic' ? { kind: 'arabic', index: bestF } : { kind: 'figure', index: bestF }, dist: bestFd };
    }
    // Otherwise, the constellation under the finger.
    if (!constellations || !(set.constellationLines || set.constellationNames || set.constellationBounds)) return null;
    const { ra, dec } = vecToRaDec(eqj);
    const c = A.Constellation(ra / 15, dec);
    const idx = this.cons.constellations.findIndex((k) => k.id === c.symbol);
    return idx >= 0 ? { sel: { kind: 'constellation', index: idx }, dist: radius } : null;
  }

  /** Equatorial J2000 direction of a star index (for info panels). */
  starDir(i: number): Vec3 {
    const d = this.stars.dir;
    return [d[i * 3], d[i * 3 + 1], d[i * 3 + 2]];
  }

  get context() {
    return this.ctx;
  }

  /** Light left by any eclipse in progress (1 = none). */
  get eclipseLight(): { sun: number; moon: number } {
    return { sun: this.eclipse.sunLight, moon: this.eclipse.moonLight };
  }

  /** Whether part of the Moon is in Earth's umbra now: a lunar eclipse beyond the faint penumbral kind. */
  get moonInUmbra(): boolean {
    const l = this.eclipse.lunar;
    return !!l && l.sep < l.umbra + l.moonR;
  }

  get magneticDeclination() {
    return this.orientation.declination;
  }

  /** Current ENU camera matrix (for tests/debug). */
  get camToEnu(): Mat3 {
    return this.camera.camToEnu;
  }

  /** ENU unit vector for a J2000 RA/Dec (degrees). */
  enuFromRaDec(ra: number, dec: number): Vec3 {
    return this.frame.toEnu(raDecToVec(ra, dec));
  }

  static font = FAMILY;
}
